import { Promotion } from '@shared/types';
import { formatPrice } from '@shared/utils';
import { useState } from 'react';
import { useI18n } from 'src/i18n/i18nContext';
import { promotionAPI } from 'src/services/api';

type Props = {
  promoCode: string;
  setPromoCode: React.Dispatch<React.SetStateAction<string>>;
  appliedPromoCode: boolean;
  setAppliedPromoCode: React.Dispatch<React.SetStateAction<boolean>>;
  promotion: Promotion | null;
  setPromotion: React.Dispatch<React.SetStateAction<Promotion | null>>;
  onRemovePromoCode: () => void;
  getPromoPricingSummary: () => {
    subtotalBeforePromo: number;
    totalAfterPromo: number;
    promoDiscountAmount: number;
  };
};

const PromoCode: React.FC<Props> = (props) => {
  const {
    appliedPromoCode,
    setAppliedPromoCode,
    promoCode,
    setPromoCode,
    promotion,
    setPromotion,
    onRemovePromoCode,
    getPromoPricingSummary,
  } = props;

  const [promoError, setPromoError] = useState<string | null>(null);
  const [applyingPromo, setApplyingPromo] = useState(false);

  const { t } = useI18n();

  const { subtotalBeforePromo, totalAfterPromo, promoDiscountAmount } = getPromoPricingSummary();

  const handleApply = async () => {
    if (!promoCode.trim()) {
      setPromoError(t.promo?.errors?.missingCode);
      return;
    }

    setApplyingPromo(true);
    setPromoError(null);

    try {
      const response = await promotionAPI.validate(promoCode);
      if (!response) {
        throw new Error(t.checkout.invalidCode);
      }

      setPromotion(response?.data);
      setAppliedPromoCode(true);
      setPromoError(null);
    } catch (err: any) {
      console.log('err', err);
      setPromoError(t.promo?.errors?.validationError);
      setAppliedPromoCode(false);
      setPromotion(null);
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    onRemovePromoCode();
    setPromoError(null);
  };

  return (
    <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
      <h3 className="mb-4 text-lg font-semibold">{t.promo?.code}</h3>
      <div className="space-y-4">
        {!appliedPromoCode ? (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">{t.promo?.have}</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder={t.promo?.placeholder}
                className={`flex-1 rounded-md border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 ${
                  promoError ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={applyingPromo}
              />
              <button
                onClick={handleApply}
                disabled={applyingPromo || !promoCode.trim()}
                className={`rounded-md px-4 py-2 font-medium ${
                  applyingPromo || !promoCode.trim()
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                {applyingPromo ? t.booking.applying : t.booking.apply}
              </button>
            </div>
            {promoError && <p className="mt-1 text-sm text-red-600">{promoError}</p>}
          </div>
        ) : (
          <div className="rounded-md border border-green-200 bg-green-50 p-4">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <p className="font-medium text-green-800">{t.promo?.applied}</p>
                {promotion && (
                  <div className="mt-1 text-green-700">
                    <p className="text-md mt-2 text-green-800">
                      {t.checkout?.subtotal}: {formatPrice(subtotalBeforePromo)}
                    </p>
                    <p className="mt-1 text-lg font-bold">
                      {t.checkout?.total}: {formatPrice(totalAfterPromo)}
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={handleRemovePromo}
                className="text-sm font-medium text-red-600 hover:text-red-800"
              >
                {t.booking.remove}
              </button>
            </div>
            {promotion?.service_discount_pct && (
              <div className="mt-3 space-x-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                <span>
                  {`${promotion.service_discount_pct}% ${t.checkout.promotionCodeDiscount}`}
                </span>
                <span>-{formatPrice(promoDiscountAmount)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default PromoCode;
